/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import type Mithril from 'mithril';

// Set only by older revisions of glowingblue/orell-thalia-common's modal manager, which ship no
// typings for it — so the guards below are falsy against the current dependency set.
const modalManager = (): { modalClosing: boolean } | undefined => (app as any).gb_modalManagerComponent;

// Optional, as core's own modals declare their attrs, to stay assignable to `UnsafeModalClass`.
// Both are always supplied, hence the non-null assertions below.
export interface ConfirmModalAttrs extends IInternalModalAttrs {
	message?: Mithril.Children;
	/** Settled with the user's answer — also on dismissal, which counts as a decline. */
	resolve?: (confirmed: boolean) => void;
}

interface ConfirmModal {
	/** Undefined while the modal is open, so `onbeforeremove` can tell dismissal from an answer. */
	confirm?: boolean;
}

class ConfirmModal extends Modal<ConfirmModalAttrs> {
	/**
	 * No title — the question is the `message`. Still needs an implementation, because `Modal`'s
	 * `view()` calls `title()` unconditionally.
	 */
	title() {
		return '';
	}

	oninit(vnode: Mithril.Vnode<ConfirmModalAttrs, this>) {
		super.oninit(vnode);

		const manager = modalManager();
		if (manager) {
			manager.modalClosing = false;
		}
	}

	className() {
		return 'ConfirmModal Modal--small';
	}

	content() {
		return [
			<div className="Modal-body">
				<div className="ConfirmModal-form">
					<div className="Form-group">
						<p>{this.attrs.message}</p>
					</div>
					<div className="Form-group ConfirmButtons">
						<Button className="Button Button--secondary" onclick={() => this.decline()}>
							{app.translator.trans('glowingblue-orell-rezensionen.forum.export.confirm_modal.no')}
						</Button>
						<Button className="Button Button--primary" type="submit">
							{app.translator.trans('glowingblue-orell-rezensionen.forum.export.confirm_modal.yes')}
						</Button>
					</div>
				</div>
			</div>,
		];
	}

	onsubmit(e: SubmitEvent) {
		e.preventDefault();
		this.attrs.resolve!(true);
		this.confirm = true;

		// Makes sure the modal actually closes.
		const manager = modalManager();
		if (manager) {
			manager.modalClosing = false;
		}

		this.hide();
	}

	decline() {
		this.attrs.resolve!(false);
		this.confirm = false;

		// Makes sure the modal actually closes.
		const manager = modalManager();
		if (manager) {
			manager.modalClosing = false;
		}

		this.hide();
	}

	onbeforeremove(vnode: Mithril.VnodeDOM<ConfirmModalAttrs, this>) {
		super.onbeforeremove(vnode);

		if (typeof this.confirm === 'undefined') {
			this.attrs.resolve!(false);
		}
	}
}

export default ConfirmModal;
